import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { CreationType } from 'src/containers/PublishProjectDrawer/types';
import * as copy from 'src/templates/copy';
import { PublishFileUpload } from 'src/utils/export/types';

export type PublishFileUploadResponse = PublishProjectResponse & {
  id: string;
};

export const publishFileUploads = async ({
  databaseId,
  exports,
}: {
  databaseId: string;
  exports: PublishFileUpload[];
}) => {
  const namedGraphsToClear = Array.from(
    exports.reduce<Set<string>>(
      (acc, { mappingInfo: { creationType, namedGraphIri } }) => {
        if (creationType === CreationType.REPLACE && namedGraphIri) {
          acc.add(namedGraphIri);
        }
        return acc;
      },
      new Set()
    )
  );

  const namedGraphResponses = await Promise.all(
    namedGraphsToClear.map((namedGraph) =>
      databasesStardogRequestDispatchers.clearDatabaseNamedGraph({
        databaseId,
        namedGraph,
      })
    )
  );

  const importPromises = exports.map(
    ({
      id,
      resourceName,
      files,
      mappingInfo: { namedGraphIri: namedGraph },
    }) => {
      const { csvOptions, mappingsOptions, propertiesOptions } = files;
      const blob = new Blob([csvOptions.contents], {
        type: csvOptions.mimeType,
      });
      const file = new File([blob], csvOptions.filename);
      const mappings = mappingsOptions.contents;
      const properties = propertiesOptions.contents;

      const namedGraphResponse = namedGraphResponses.find(
        (response) => response.payload.action.namedGraph === namedGraph
      );
      if (
        namedGraphResponse?.type ===
        DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_FAILURE
      ) {
        return Promise.resolve<PublishFileUploadResponse>({
          id,
          action: namedGraphResponse,
          success: false,
          actionDescription: copy.notifications.database.clearNamedGraph,
          message:
            copy.notifications.publishProject.importCsv.failure(resourceName),
        });
      }

      return new Promise<PublishFileUploadResponse>((res) =>
        databasesStardogRequestDispatchers
          .importCsv({
            databaseId,
            file,
            mappings,
            namedGraph,
            properties,
          })
          .then((response) =>
            res(
              response.type === DatabasesActionType.IMPORT_CSV_SUCCESS
                ? {
                    id,
                    action: response,
                    success: true,
                    message:
                      copy.notifications.publishProject.importCsv.success(
                        resourceName
                      ),
                  }
                : {
                    id,
                    action: response,
                    success: false,
                    actionDescription: copy.notifications.database.importCsv,
                    message:
                      copy.notifications.publishProject.importCsv.failure(
                        resourceName
                      ),
                  }
            )
          )
      );
    }
  );

  return Promise.all(importPromises);
};
